import { getPinCounterLabel } from './getPinCounterLabel';
import { EIcon, IOffer } from '../../../repositories/dailyrent/entities/offer/Offer';
import { IFeatureProperties } from '../../../types/map';
import { getMinPriceOffer } from '../../getMinPriceOffer';

/** Иконка в пилюле пина: сердечко избранного или иконка объявления */
export type TPinIcon = 'favorite' | EIcon;

export interface IPinViewModel {
  /** Цена в пилюле; null — пин не рисуется */
  price: number | null;
  /** Показывать префикс «от»: цена минимальная из нескольких */
  isPriceFrom: boolean;
  isViewed: boolean;
  /** Хотя бы одно объявление в избранном: поднимает пин над обычными (см. getBalloonZIndex) */
  isFavorite: boolean;
  /** Единственная иконка пина (см. getPinIcon); null — без иконки */
  icon: TPinIcon | null;
  /** Пин объединяет несколько объявлений: у него каунтер, цена «от», своя вёрстка пилюли */
  isMultiPin: boolean;
  /** Количество объектов в пине - для мультипинов */
  counterLabel: string | null;
}

/** При разных иконках объявлений пин показывает самую приоритетную: скидка важнее молнии */
const OFFER_ICONS_PRIORITY: EIcon[] = [EIcon.Discount, EIcon.Bolt];

/** На пине одна иконка: у избранного сердечко перекрывает иконки объявлений */
function getPinIcon(offers: IOffer[], isFavorite: boolean): TPinIcon | null {
  if (isFavorite) {
    return 'favorite';
  }

  const offerIcons = new Set(offers.map(offer => offer.icon));

  return OFFER_ICONS_PRIORITY.find(priorityIcon => offerIcons.has(priorityIcon)) ?? null;
}

/**
 * Наведённое в листинге объявление фичи по realtyId из properties.hoveredOfferId;
 * чужой id или его отсутствие — наведения нет.
 */
function getHoveredOffer({ offers, hoveredOfferId }: IFeatureProperties): IOffer | null {
  if (hoveredOfferId === undefined || hoveredOfferId === null) {
    return null;
  }

  return offers.find(offer => offer.realtyId === hoveredOfferId) ?? null;
}

/**
 * Данные пилюли пина по всем объявлениям фичи.
 *
 * У одиночного пина правила вырождаются в его единственное объявление,
 * у мультипина — агрегируют весь список (минимальная цена, избранное - хотя бы одно,
 * просмотренность - все объявления).
 *
 * При наведённом объявлении (см. getHoveredOffer) пин показывает его данные
 * вместо агрегата, каунтер и просмотренность пина при этом остаются.
 */
export function getPinViewModel(properties: IFeatureProperties): IPinViewModel {
  const { offers } = properties;
  const counterLabel = getPinCounterLabel(properties);
  const isMultiPin = counterLabel !== null;
  const hoveredOffer = getHoveredOffer(properties);
  // Просмотренность - свойство пина: новое объявление в просмотренном мультипине снова делает его непросмотренным
  const isViewed = offers.length > 0 && offers.every(offer => offer.isViewed);

  if (hoveredOffer && isMultiPin) {
    return {
      // нулевая цена, как и в агрегате, считается отсутствующей
      price: hoveredOffer.averagePricePerDay || null,
      isPriceFrom: false,
      isViewed,
      isFavorite: hoveredOffer.isFavorite,
      icon: getPinIcon([hoveredOffer], hoveredOffer.isFavorite),
      counterLabel,
      isMultiPin,
    };
  }

  const isFavorite = offers.some(offer => offer.isFavorite);

  return {
    // Та же функция ставит это объявление первым в мультипине (convertClustersToMultipinFeaturesMap):
    // цена «от» и первое открытое объявление не могут разойтись
    price: getMinPriceOffer(offers)?.averagePricePerDay ?? null,
    // «от» и каунтер берутся из одного источника — они не могут разойтись
    isPriceFrom: isMultiPin,
    isViewed,
    isFavorite,
    icon: getPinIcon(offers, isFavorite),
    counterLabel,
    isMultiPin,
  };
}
